import { useQuery } from "@tanstack/react-query";
import type { UserInfoResponse } from "@dealer-center/ddc-pages-configuration-interface";
import {
  CONFIGURATION_QUERY_KEY_BASE,
  configurationRequest
} from "./configurationRequest";

/**
 * The subject's identifiers, always passed in — never resolved here.
 *
 * Invariant 4 of contracts/fetch-hooks.md: a hook that read the outlet context itself would let the
 * subject the page shows and the subject the query fetches drift apart. Both are optional because
 * the caller may render before the context resolves; the query stays disabled until both arrive.
 */
export interface UseGetUserInfoOptions {
  userId?: string;
  accountId?: string;
}

/**
 * Reads the subject's stored profile (`userinfo`) for the User Information tab (FR-012, FR-014) and
 * the identity header's account line (FR-004, sourced from `summary.company`).
 *
 * `administrator` is **not** on this response. CC9 moved the field onto feature-access between
 * 2026-08-21 and 2026-08-24, and `UserInfoResponse` deliberately does not declare it in any form —
 * reading it from here is a compile error, by design (spec SC-011). The Feature Access tab gets the
 * flag from `useGetFeatureAccess`, the same query that supplies its rows.
 *
 * `retry: 1` and a 5-minute `staleTime` match the area's other read hooks (invariant 3). The query
 * is `enabled` only once both identifiers are present, because the BFF returns 400 without them —
 * so a request we already know will fail is never fired.
 */
export const useGetUserInfo = ({ userId, accountId }: UseGetUserInfoOptions) =>
  useQuery({
    // `accountId` and `userId` are part of the key so an "as"-context switch cannot serve another
    // subject's cached profile — the Edge Case that says no tab may fall back to the signed-in
    // internal user's own data (invariant 2).
    queryKey: [CONFIGURATION_QUERY_KEY_BASE, "userinfo", accountId, userId],
    queryFn: () => {
      // Unreachable while `enabled` holds; it narrows the optional identifiers without a cast, so
      // the request builder never receives an empty string.
      if (!userId || !accountId) {
        throw new Error("useGetUserInfo requires both userId and accountId");
      }

      return configurationRequest<UserInfoResponse>({
        resource: "userinfo",
        userId,
        accountId
      });
    },
    enabled: Boolean(userId && accountId),
    retry: 1,
    staleTime: 1000 * 60 * 5
  });
